import { randomstring } from '@/utilities/utils';
import type { IDialogInstance } from './IDialogInstance';
import type { MaybeRef, Ref } from 'vue';
import { ModalButton } from '@/components/Modal/ModalButton';
import type { IDialogClosecallbackParams } from './IDialogClosecallbackParams';
import { useDialog } from './dialog';
import type { TaskLoaderState } from '../loaders';
import type { IDialogOpenedCallbackParams } from './IDialogOpenedCallbackParams';
import { $tm } from '@/locize';

// TODO: Some of this utility types are generic, so we should probably put them in some generic place

// Utility type to omit all onUpdate:* props
export type OmitOnUpdateProps<T> = {
    [K in keyof T as K extends `onUpdate:${string}` ? never : K]: T[K];
};

// Extract v-model keys from onUpdate:* props
export type ExtractVModelKeys<T> = {
    [K in keyof T as K extends `onUpdate:${infer P}` ? P : never]: Ref<any>;
};

// Utility type to omit namedVModels keys and onUpdate:* props from componentProps
export type OmitNamedVModelsProps<TProps, TNamedVModels extends Record<string, any> | undefined> =
    TNamedVModels extends Record<string, any> ? OmitOnUpdateProps<Omit<TProps, keyof TNamedVModels>> : OmitOnUpdateProps<TProps>;

type DialogButtonParams<TInstance, TResult> = Parameters<typeof ModalButton.Create<IDialogClosecallbackParams<TInstance>, TResult>>[0];

// The builder type, accumulating button result types
type DialogBuilder<
    TComponent extends new (...args: any) => any,
    TInstance extends InstanceType<TComponent> = InstanceType<TComponent>,
    TModelValue = any,
    TDialogResult = never,
> = {
    withTitle: (title: MaybeRef<string>) => DialogBuilderStep2<TComponent, TInstance, TModelValue, TDialogResult>;
};

type DialogBuilderStep2<
    TComponent extends new (...args: any) => any,
    TInstance extends InstanceType<TComponent>,
    TModelValue,
    TDialogResult,
> = {
    withBodyText: (
        bodyString: string,
        params?: Record<string, any>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult>;
    withBodyComponent: <TNewComponent extends new (...args: any) => any, TNewModelValue = any>(
        component: TNewComponent,
        options?: {
            componentProps?: OmitNamedVModelsProps<
                InstanceType<TNewComponent>['$props'],
                ExtractVModelKeys<InstanceType<TNewComponent>['$props']>
            >;
            vmodel?: Ref<TNewModelValue>;
            namedVModels?: ExtractVModelKeys<InstanceType<TNewComponent>['$props']>;
        },
    ) => DialogBuilderStep3<TNewComponent, InstanceType<TNewComponent>, TNewModelValue, TDialogResult>;
};

type DialogBuilderStep3<
    TComponent extends new (...args: any) => any,
    TInstance extends InstanceType<TComponent>,
    TModelValue,
    TDialogResult,
> = {
    withButton: <TResult>(
        params: DialogButtonParams<TInstance, TResult>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withButtons: <TResult>(
        ...params: Array<DialogButtonParams<TInstance, TResult>>
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withNegativeButton: <TResult>(
        params: DialogButtonParams<TInstance, TResult>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withCloseButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withCancelButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withNoButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withYesButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withSaveButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withOkButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withSelectButton: <TResult>(
        params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    withHideOnEscape: (
        closeAction?: (params: IDialogClosecallbackParams<TInstance>) => Promise<any>,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult>;
    withOptions: (
        options?: Partial<
            Pick<IDialogInstance<TComponent, TInstance>, 'DisableAnimation' | 'Height' | 'MinHeight' | 'Size' | 'TextAlign' | 'Type'>
        >,
    ) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult>;
    withLoadState: (loadingState?: TaskLoaderState) => DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult>;
    Open: () => Promise<IDialogOpenedCallbackParams<TInstance, TDialogResult>>;
};

export const useDialogBuilder = <
    TComponent extends new (...args: any) => any = any,
    TInstance extends InstanceType<TComponent> = InstanceType<TComponent>,
    TModelValue = any,
    TDialogResult = never,
>(): DialogBuilder<TComponent, TInstance, TModelValue, TDialogResult> => {
    const dialogInstance: IDialogInstance<TComponent, TInstance, TModelValue, TDialogResult> = {
        Title: '',
        TextAlign: 'left',
        Buttons: [],
        Size: 'sm',
        DisableAnimation: false,
        EnableHideOnEscape: false,
        UniqueLoopKey: randomstring(''),
        PromiseResolver: () => {},
        ClosePromise: Promise.resolve(undefined as unknown as TDialogResult), // Temp value so we dont care if the type is forced
    };

    const withTitle = (title: MaybeRef<string>) => {
        dialogInstance.Title = title;
        return stepTwo;
    };

    const withBodyText = (bodyString: string, params?: Record<string, any>) => {
        (dialogInstance as any).BodyString = bodyString;
        if (params) {
            (dialogInstance as any).BodyStringParams = params;
        }
        return stepThree;
    };

    const withBodyComponent = <TNewComponent extends new (...args: any) => any, TNewModelValue = any>(
        component: TNewComponent,
        options?: {
            componentProps?: OmitNamedVModelsProps<
                InstanceType<TNewComponent>['$props'],
                ExtractVModelKeys<InstanceType<TNewComponent>['$props']>
            >;
            vmodel?: Ref<TNewModelValue>;
            namedVModels?: ExtractVModelKeys<InstanceType<TNewComponent>['$props']>;
        },
    ) => {
        (dialogInstance as any).BodyComponent = component;
        if (options?.componentProps) {
            (dialogInstance as any).BodyComponentProps = options.componentProps;
        }
        if (options?.vmodel) {
            (dialogInstance as any).VModel = options.vmodel;
        }
        if (options?.namedVModels) {
            dialogInstance.NamedVModels = options.namedVModels;
        }

        return stepThree as DialogBuilderStep3<TNewComponent, InstanceType<TNewComponent>, TNewModelValue, TDialogResult>;
    };

    const withButton = <TResult>(params: DialogButtonParams<TInstance, TResult>) => {
        const button = ModalButton.Create<IDialogClosecallbackParams<TInstance>, TResult>(params);
        dialogInstance.Buttons.push(button);
        return stepThree as DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    };

    const withButtons = <TResult>(...params: Array<DialogButtonParams<TInstance, TResult>>) => {
        params.forEach((p) => {
            const button = ModalButton.Create<IDialogClosecallbackParams<TInstance>, TResult>(p);
            dialogInstance.Buttons.push(button);
        });
        return stepThree as DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    };

    const withNegativeButton = <TResult>(params: DialogButtonParams<TInstance, TResult>) => {
        dialogInstance.EnableHideOnEscape = true;
        if (params.action) {
            dialogInstance.UserClosedAction = params.action;
        }

        const button = ModalButton.Create<IDialogClosecallbackParams<TInstance>, TResult>(params);
        dialogInstance.Buttons.push(button);
        return stepThree as DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult | TResult>;
    };

    const withCloseButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withNegativeButton<TResult>({
            ...params,
            text: $tm('button.close'),
            buttonType: 'Secondary',
        });
    };

    const withCancelButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withNegativeButton<TResult>({
            ...params,
            text: $tm('button.cancel'),
            buttonType: 'Secondary',
        });
    };

    const withNoButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withNegativeButton<TResult>({
            ...params,
            text: $tm('button.no'),
            buttonType: 'Danger',
        });
    };

    const withYesButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withButton<TResult>({
            ...params,
            text: $tm('button.yes'),
            buttonType: 'Primary',
        });
    };

    const withSaveButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withButton<TResult>({
            ...params,
            text: $tm('button.save'),
            buttonType: 'Primary',
        });
    };

    const withOkButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withButton<TResult>({
            ...params,
            text: $tm('button.ok'),
            buttonType: 'Primary',
        });
    };

    const withSelectButton = <TResult>(params: Omit<DialogButtonParams<TInstance, TResult>, 'text' | 'buttonType'>) => {
        return withButton<TResult>({
            ...params,
            text: $tm('button.select'),
            buttonType: 'Primary',
        });
    };

    const withHideOnEscape = (closeAction?: (params: IDialogClosecallbackParams<TInstance>) => Promise<any>) => {
        dialogInstance.EnableHideOnEscape = true;
        if (closeAction) {
            dialogInstance.UserClosedAction = closeAction;
        }
        return stepThree;
    };

    const withOptions = (
        options?: Partial<
            Pick<IDialogInstance<TComponent, TInstance>, 'DisableAnimation' | 'Height' | 'MinHeight' | 'Size' | 'TextAlign' | 'Type'>
        >,
    ) => {
        if (!options) {
            return stepThree;
        }
        if (options.DisableAnimation !== undefined) {
            dialogInstance.DisableAnimation = options.DisableAnimation;
        }
        if (options.Height !== undefined) {
            (dialogInstance as any).Height = options.Height;
        }
        if (options.MinHeight !== undefined) {
            (dialogInstance as any).MinHeight = options.MinHeight;
        }
        if (options.Size !== undefined) {
            dialogInstance.Size = options.Size;
        }
        if (options.TextAlign !== undefined) {
            dialogInstance.TextAlign = options.TextAlign;
        }
        if (options.Type !== undefined) {
            (dialogInstance as any).Type = options.Type;
        }
        return stepThree;
    };

    const withLoadState = (loadingState?: TaskLoaderState) => {
        if (loadingState) {
            (dialogInstance as any).LoadingState = loadingState;
        }
        return stepThree;
    };

    const Open = () => {
        return useDialog().OpenDialog<TComponent, TInstance, TModelValue, TDialogResult>(dialogInstance);
    };

    const stepOne: DialogBuilder<TComponent, TInstance, TModelValue, TDialogResult> = {
        withTitle,
    };

    const stepTwo: DialogBuilderStep2<TComponent, TInstance, TModelValue, TDialogResult> = {
        withBodyComponent,
        withBodyText,
    };

    const stepThree: DialogBuilderStep3<TComponent, TInstance, TModelValue, TDialogResult> = {
        withButton,
        withButtons,
        withNegativeButton,
        withCloseButton,
        withCancelButton,
        withNoButton,
        withYesButton,
        withSaveButton,
        withOkButton,
        withSelectButton,
        withHideOnEscape,
        withOptions,
        withLoadState,
        Open,
    };

    return stepOne;
};
